// A "Ducks"-style collection
// https://github.com/erikras/ducks-modular-redux

import { reject } from 'lodash';
import { getMessage } from 'util/locale';

// selectors
import {
  getAvailableYieldLayers,
  getExcludedLayersParam,
  getIncludedCciLayers,
  getIncludedYieldLayers,
  getSupportedCropLayersForLayers
} from 'advanced/selectors';
import {
  getPlantingPlanParamsForTemplateCall
} from 'planting-plan-form/selectors';

// actions
import {
  validateIncludedCciLayersForRecommendations,
  validateIncludedYieldLayersForRecommendations
} from 'current/validators';
import { updatePlantingPlanNumZones } from 'planting-plan-form';
import { startScriptFlow } from 'script-flow';
import advancedScriptFlowDefinition from 'advanced/scriptFlowDefinition';
import { setValidationResult } from 'validations';

import { addMessage } from 'messages';
import { messageTypes } from 'constants/values';


// Action Types
export const typePrefix = 'vra/advanced/';
export const actions = [
  'CLEAR_HARVEST_HISTORY',
  'SET_AVAILABILITY',
  'SET_STEP',
  'SET_HARVEST_CROP',
  'SET_HARVEST_DATE',
  'SET_EXCLUDED_LAYERS',
  'CLEAR_EXCLUDED_LAYERS',
  'SET_RECOMMENDED_NUM_ZONES',
  'CLEAR_RECOMMENDED_NUM_ZONES',
  'SET_HAS_DEAD_ZONE',

  // analytics
  'CANCEL_ADVANCED_FLOW',
  'CHOOSE_SCRIPT_TYPE',
  'IMPORT_YIELD',
  'NAVIGATE_ADVANCED_FLOW',
  'FINISH_SCRIPT',
  'READ_HELP',
  'REMOVE_LAYER'
].reduce((result, key) => { result[key] = `${typePrefix}${key}`; return result; }, {});
export const nav = [
  'BACK',
  'FORWARD'
].reduce((result, key) => { result[key] = key; return result; }, {});

// Reducer

export default function reducer( // NOSONAR False-Positive, Reducer Exception
  state = {
    step: 1,
    params: {
      harvestHistory: { },
      excludedLayers: [ ]
    }
  },
  action = {}
) {
  switch (action.type) {
    case actions.SET_AVAILABILITY:
      return {
        ...state,
        availability: action.payload.availability
      };

    case actions.SET_STEP:
      return {
        ...state,
        step: action.payload.newStep
      };

    case actions.CLEAR_HARVEST_HISTORY:
      return {
        ...state,
        params: {
          ...state.params,
          harvestHistory: { }
        }
      };

    case actions.SET_HARVEST_CROP:
      return {
        ...state,
        params: {
          ...state.params,
          harvestHistory: {
            ...state.params.harvestHistory,
            [action.payload.season]: {
              ...state.params.harvestHistory[action.payload.season],
              cropId: action.payload.cropId
            }
          }
        }
      };
    
    case actions.SET_HARVEST_DATE:
      return {
        ...state,
        params: {
          ...state.params,
          harvestHistory: {
            ...state.params.harvestHistory,
            [action.payload.season]: {
              ...state.params.harvestHistory[action.payload.season],
              date: action.payload.date
            }
          }
        }
      };

    case actions.SET_EXCLUDED_LAYERS:
      return {
        ...state,
        params: {
          ...state.params,
          excludedLayers: action.payload.layers
        }
      };


    case actions.CLEAR_EXCLUDED_LAYERS:
      return {
        ...state,
        params: {
          ...state.params,
          excludedLayers: []
        }
      };

    case actions.SET_RECOMMENDED_NUM_ZONES:
      return {
        ...state,
        params: {
          ...state.params,
          recommendedNumZones: action.payload.recommendedNumZones
        }
      };

    case actions.CLEAR_RECOMMENDED_NUM_ZONES:
      return {
        ...state,
        params: {
          ...state.params,
          recommendedNumZones: null
        }
      };

    case actions.SET_HAS_DEAD_ZONE:
      return {
        ...state,
        params: {
          ...state.params,
          hasDeadZone: action.payload.hasDeadZone
        }
      };

    default:
      return state;
  }
}

// Actions

export const validateIncludedLayers = () => {
  return (dispatch, getState) => {
    const state = getState();
    const cciLayers = getIncludedCciLayers(state);
    const supportedCropCciLayers = getSupportedCropLayersForLayers(state, cciLayers);
    const rawCciValidatorResult = validateIncludedCciLayersForRecommendations({
      cciLayers, supportedCropCciLayers
    });
    const yieldLayers = getIncludedYieldLayers(state);
    const supportedCropYieldLayers = getSupportedCropLayersForLayers(state, yieldLayers);
    const rawYieldValidatorResult = validateIncludedYieldLayersForRecommendations({
      yieldLayers, supportedCropYieldLayers
    });
    dispatch(setValidationResult({ key: 'cciLayers', rawValidatorResult: rawCciValidatorResult }));
    dispatch(setValidationResult({ key: 'yieldLayers', rawValidatorResult: rawYieldValidatorResult }));
  };
};

export const setHarvestCrop = ({season, cropId}) => {
  return (dispatch) => {
    dispatch({
      type: actions.SET_HARVEST_CROP,
      payload: { season, cropId }
    });
    dispatch(validateIncludedLayers());
  };
};

export const setHarvestDate = ({season, date}) => {
  return (dispatch) => {
    dispatch({
      type: actions.SET_HARVEST_DATE,
      payload: { season, date }
    });
    dispatch(validateIncludedLayers());
  };
};

export const clearHarvestHistory = () => ({
  type: actions.CLEAR_HARVEST_HISTORY
});

export const initHarvestHistory = () => {
  return (dispatch, getState) => {
    dispatch(clearHarvestHistory());

    // Set harvest history for seasons with yield data. CCI-only years will be undefined until user chooses a crop
    // during advanced script flow.
    const yieldLayers = getAvailableYieldLayers(getState());
    yieldLayers.forEach(yieldLayer => {
      dispatch(setHarvestCrop({season:yieldLayer.season, cropId:yieldLayer.cropId}));
      dispatch(setHarvestDate({season:yieldLayer.season, date:yieldLayer.date}));
    });
  };
};

export const setRecommendedNumZones = (recommendedNumZones) => ({
  type: actions.SET_RECOMMENDED_NUM_ZONES,
  payload: { recommendedNumZones }
});

export const clearRecommendedNumZones = () => ({
  type: actions.CLEAR_RECOMMENDED_NUM_ZONES
});

export const setAdvancedFlowStep = (newStep) => ({
  type: actions.SET_STEP,
  payload: { newStep }
});

export const setAvailability = (availability) => ({
  type: actions.SET_AVAILABILITY,
  payload: { availability }
});

// This function won't be called for deep links to script flow. So keep flow init code in stepActions.js.
export const startAdvancedFlow = () =>
  startScriptFlow({definition:advancedScriptFlowDefinition});

export const clearExcludedLayers = () => {
  return {
    type: actions.CLEAR_EXCLUDED_LAYERS
  };
};

export const setExcludedLayersParam = (layers, select) => {
  return {
    type: actions.SET_EXCLUDED_LAYERS,
    payload: { layers, select }
  };
};

export const includeLayer = (layer) => {
  return (dispatch, getState) => {
    const state = getState();
    let excludedLayers = getExcludedLayersParam(state);
    excludedLayers = reject(excludedLayers, ['season', layer]);
    dispatch(setExcludedLayersParam(excludedLayers, true));
    dispatch(validateIncludedLayers());
  };
};

export const excludeLayers = (layers) => {
  return (dispatch, getState) => {
    const state = getState();
    const excludedLayers = getExcludedLayersParam(state);
    dispatch(setExcludedLayersParam(excludedLayers.concat(layers)));
    dispatch(validateIncludedLayers());
  };
};

export const setHasDeadZone = (hasDeadZone) => {
  return {
    type: actions.SET_HAS_DEAD_ZONE,
    payload: { hasDeadZone }
  };
};

export const checkForZoneCountMismatch = template => {
  // SC-2669
  return (dispatch, getState) => {
    const state = getState();
    const params = getPlantingPlanParamsForTemplateCall(state);
    const requestedNumZones = params.numZones;
    const templateNumZones = Object.keys(template.zones).length;
    if(requestedNumZones && requestedNumZones !== templateNumZones) {
      params.numZones = templateNumZones;
      dispatch(updatePlantingPlanNumZones(templateNumZones));

      const text = getMessage('help-text__zone-count-mismatch');
      dispatch(addMessage({messageType: messageTypes.SUCCESS, text, toast: false }));
    }
  };
};
